import { useCaseStore } from '@/stores/case'
import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { CaseDTO } from '@/generated'
import { useUserStore } from '@/stores/user'

interface RoutingAvailability {
  routine: boolean
  urgent: boolean
  emergency: boolean
}

function useRoutingAvailability() {
  const caseStore = useCaseStore()
  const userStore = useUserStore()
  const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()

  const { hasGodModeShift } = storeToRefs(userStore)
  const { caseList } = storeToRefs(caseStore)
  const { casesWithVitalSigns } = storeToRefs(patientsWithVitalSignsStore)
  const routingAvailabilityPerCaseId = ref<Map<string, RoutingAvailability>>(new Map())

  watch(caseList, (newList) => {
    setRoutingAvailability(newList)
  })

  watch(casesWithVitalSigns, (newList) => {
    const cases = newList.map((item) => item.case)
    setRoutingAvailability(cases)
  })

  function setRoutingAvailability(cases: CaseDTO[]) {
    cases.forEach((caseItem) => {
      const routingAvailability: RoutingAvailability = {
        emergency: !(caseItem.emergencyRoutingUnavailable || hasGodModeShift.value),
        routine: !caseItem.routineRoutingUnavailable,
        urgent: !caseItem.urgentRoutingUnavailable
      }

      routingAvailabilityPerCaseId.value.set(caseItem.id, routingAvailability)
    })
  }

  const isEmergencyRoutingAvailable = computed(() =>
    Array.from(routingAvailabilityPerCaseId.value.values()).some((caseItem) => caseItem.emergency)
  )

  return {
    isEmergencyRoutingAvailable,
    routingAvailabilityPerCaseId
  }
}

export default useRoutingAvailability
